"use client";

import { Suspense, useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { useGetListingList, useGetFilterCounts, useToggleFavorite } from "@/lib/api/hooks";
import { useI18n, getLocalizedField } from "@/i18n";
import { useLangHref } from "@/i18n/useLangHref";

const formatPrice = (price) => {
  if (price == null || isNaN(price)) return "-";
  return `GNF ${(price / 1000000).toFixed(0)}M`;
};

// Next.js requires useSearchParams to be inside a Suspense boundary
export default function SearchPage() {
  return (
    <Suspense fallback={null}>
      <SearchPageInner />
    </Suspense>
  );
}

function SearchPageInner(props) {
  const { t, lang } = useI18n();
  const langHref = useLangHref();

  const SORT_OPTIONS = useMemo(() => [
    { value: "latest", label: t("search.sortLatest") },
    { value: "price_asc", label: t("search.sortPriceAsc") },
    { value: "price_desc", label: t("search.sortPriceDesc") },
    { value: "views", label: t("search.sortViews") },
  ], [t]);

  const getFuelLabel = (type) => {
    const map = { petrol: t("car.petrol"), diesel: t("car.diesel"), hybrid: t("car.hybrid"), electric: t("car.electric") };
    return map[type] || type || "";
  };
  const getTransLabel = (type) => {
    const map = { automatic: t("car.auto"), manual: t("car.manual") };
    return map[type] || type || "";
  };
  const getConditionLabel = (type) => {
    const map = { new: t("car.evNew"), used: t("common.all") };
    return map[type] || type || "";
  };

  const [pageNum, setPageNum] = useState(1);
  const [pageSize] = useState(12);
  const [categoryId, setCategoryId] = useState(null);
  const [brandId, setBrandId] = useState(null);
  const [cityId, setCityId] = useState(null);
  const searchParams = useSearchParams();
  const [keyword, setKeyword] = useState(() => searchParams.get("q") || "");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [minYear, setMinYear] = useState("");
  const [maxYear, setMaxYear] = useState("");
  const [fuelType, setFuelType] = useState(null);
  const [transmission, setTransmission] = useState(null);
  const [condition, setCondition] = useState(null);
  const [sort, setSort] = useState("latest");

  const params = { pageNum, pageSize, sort };
  if (categoryId) params.categoryId = categoryId;
  if (brandId) params.brandId = brandId;
  if (cityId) params.cityId = cityId;
  if (keyword.trim()) params.keyword = keyword.trim();
  if (minPrice) params.minPrice = Number(minPrice);
  if (maxPrice) params.maxPrice = Number(maxPrice);
  if (minYear) params.minCarYear = Number(minYear);
  if (maxYear) params.maxCarYear = Number(maxYear);
  if (fuelType) params.fuelType = fuelType;
  if (transmission) params.transmission = transmission;
  if (condition) params.condition = condition;

  const { data, isLoading } = useGetListingList(params);
  const { data: filterData } = useGetFilterCounts();
  const filters = filterData?.data ?? filterData ?? {};

  const records = data?.data ?? [];
  const totalPages = data?.pagination?.total ? Math.ceil(data.pagination.total / pageSize) : 1;
  const totalCount = data?.pagination?.total ?? 0;

  // 收藏状态管理
  const [favoritedIds, setFavoritedIds] = useState(new Set());
  const [toast, setToast] = useState(null);
  const toggleFavMutation = useToggleFavorite();

  useEffect(() => {
    if (records.length > 0) {
      setFavoritedIds(new Set(records.filter(r => r.isFavorite === 1).map(r => r.id)));
    }
  }, [records]);

  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const handleFavorite = useCallback(async (e, listingId) => {
    e.preventDefault();
    e.stopPropagation();
    const wasFavorited = favoritedIds.has(listingId);
    // 乐观更新
    setFavoritedIds(prev => {
      const next = new Set(prev);
      if (wasFavorited) next.delete(listingId); else next.add(listingId);
      return next;
    });
    try {
      await toggleFavMutation.mutateAsync(listingId);
      showToast(wasFavorited ? t("car.favorites") + " removed" : t("car.favorites") + " saved");
    } catch (err) {
      console.error('[favorite] toggle error:', err);
      // 回滚
      setFavoritedIds(prev => {
        const next = new Set(prev);
        if (wasFavorited) next.add(listingId); else next.delete(listingId);
        return next;
      });
      showToast(err?.message || t("common.networkError"), "error");
    }
  }, [favoritedIds, toggleFavMutation, showToast]);

  // 收集已激活的筛选条件用于显示 chips
  const activeFilters = [];
  if (brandId) {
    const brand = (filters.brands || []).find(b => b.id === brandId);
    if (brand) activeFilters.push(getLocalizedField(brand, "name", lang));
  }
  if (categoryId) {
    const cat = (filters.categories || []).find(c => c.id === categoryId);
    if (cat) activeFilters.push(getLocalizedField(cat, "name", lang));
  }
  if (cityId) {
    const city = (filters.cities || []).find(c => c.id === cityId);
    if (city) activeFilters.push(getLocalizedField(city, "name", lang));
  }
  if (condition) activeFilters.push(getConditionLabel(condition));
  if (fuelType) activeFilters.push(getFuelLabel(fuelType));
  if (transmission) activeFilters.push(getTransLabel(transmission));
  if (minPrice || maxPrice) activeFilters.push(`${t("search.priceRange")}: ${minPrice || "0"}-${maxPrice || t("common.any")}`);
  if (minYear || maxYear) activeFilters.push(`${t("search.yearRange")}: ${minYear || t("common.any")}-${maxYear || t("common.any")}`);

  const removeFilter = (type, value) => {
    if (type === "brand") { setBrandId(null); }
    else if (type === "category") { setCategoryId(null); }
    else if (type === "city") { setCityId(null); }
    else if (type === "condition") { setCondition(null); }
    else if (type === "fuel") { setFuelType(null); }
    else if (type === "trans") { setTransmission(null); }
  };

  const clearAll = () => {
    setCategoryId(null);
    setBrandId(null);
    setCityId(null);
    setMinPrice("");
    setMaxPrice("");
    setMinYear("");
    setMaxYear("");
    setFuelType(null);
    setTransmission(null);
    setCondition(null);
    setPageNum(1);
  };

  const handleApplyFilter = () => {
    setPageNum(1);
  };

  const toggleFilter = (setter, value) => {
    setter(prev => prev === value ? null : value);
  };

  return (
    <>
      <div className="page-header">
        <div className="container">
          <div className="breadcrumb">
            <Link href={langHref("/")}>{t("nav.home")}</Link>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
              <path d="m9 18 6-6-6-6"/>
            </svg>
            <span>{t("nav.buy")}</span>
          </div>
          <h1 className="page-title">{t("search.title")}</h1>
          <p className="page-subtitle">{t("search.subtitle", { total: totalCount.toLocaleString() })}</p>
        </div>
      </div>

      <div className="container">
        <div className="filter-bar" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "16px 16px" }}>
          {activeFilters.length > 0 ? activeFilters.map((f, i) => (
            <span key={i} className="filter-chip active" style={{ cursor: "default", display: "inline-flex", alignItems: "center", gap: 4 }}>
              {f}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12" style={{ cursor: "pointer" }} onClick={clearAll}>
                <path d="M18 6 6 18M6 6l12 12"/>
              </svg>
            </span>
          )) : <span style={{ fontSize: 13, color: "var(--color-text-muted)" }}>{t("search.filters")}: {t("common.none")}</span>}
          {activeFilters.length > 0 && (
            <span className="clear" onClick={clearAll} style={{ fontSize: 12, cursor: "pointer", color: "var(--color-primary)", marginLeft: 4 }}>{t("search.reset")}</span>
          )}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--color-text-secondary)" }}>
            <span>{t("search.sortBy")}:</span>
            <select
              value={sort}
              onChange={e => { setSort(e.target.value); setPageNum(1); }}
              style={{ border: "none", background: "transparent", fontWeight: 600, color: "var(--color-text)", cursor: "pointer", fontSize: 13 }}
            >
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="list-layout">
          <aside className="filter-sidebar">
            {/* 价格区间 */}
            <div className="filter-group">
              <div className="filter-group-title">
                {t("search.priceRange")} (USD)
                <span className="clear" onClick={() => { setMinPrice(""); setMaxPrice(""); }}>{t("search.reset")}</span>
              </div>
              <div className="range-input">
                <input type="text" placeholder={t("common.none")} value={minPrice} onChange={e => setMinPrice(e.target.value)}/>
                <span>—</span>
                <input type="text" placeholder={t("common.none")} value={maxPrice} onChange={e => setMaxPrice(e.target.value)}/>
              </div>
            </div>

            {/* 年份 */}
            <div className="filter-group">
              <div className="filter-group-title">{t("search.yearRange")}</div>
              <div className="range-input">
                <input type="text" placeholder={t("common.none")} value={minYear} onChange={e => setMinYear(e.target.value)}/>
                <span>—</span>
                <input type="text" placeholder={t("common.none")} value={maxYear} onChange={e => setMaxYear(e.target.value)}/>
              </div>
            </div>

            {/* 品牌 */}
            {(filters.brands || []).length > 0 && (
              <div className="filter-group">
                <div className="filter-group-title">
                  {t("hero.brand")}
                  {brandId && <span className="clear" onClick={() => { setBrandId(null); setPageNum(1); }}>{t("search.reset")}</span>}
                </div>
                <div className="filter-options">
                  {(filters.brands || []).map(b => (
                    <label key={b.id} className="filter-option" style={{ cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={brandId === b.id}
                        onChange={() => toggleFilter(setBrandId, b.id)}
                      />
                      {" "}{getLocalizedField(b, "name", lang)}
                      <span className="count">{b.count.toLocaleString()}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* 车况 */}
            {filters.conditions && Object.keys(filters.conditions).length > 0 && (
              <div className="filter-group">
                <div className="filter-group-title">
                  {t("search.fuelType")}
                  {condition && <span className="clear" onClick={() => { setCondition(null); setPageNum(1); }}>{t("search.reset")}</span>}
                </div>
                <div className="filter-options">
                  {Object.entries(filters.conditions).map(([key, count]) => (
                    <label key={key} className="filter-option" style={{ cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={condition === key}
                        onChange={() => toggleFilter(setCondition, key)}
                      />
                      {" "}{getConditionLabel(key)}
                      <span className="count">{count.toLocaleString()}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* 车身类型 */}
            {(filters.categories || []).length > 0 && (
              <div className="filter-group">
                <div className="filter-group-title">
                  {t("sections.browseByCategory")}
                  {categoryId && <span className="clear" onClick={() => { setCategoryId(null); setPageNum(1); }}>{t("search.reset")}</span>}
                </div>
                <div className="filter-options">
                  {(filters.categories || []).map(c => (
                    <label key={c.id} className="filter-option" style={{ cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={categoryId === c.id}
                        onChange={() => toggleFilter(setCategoryId, c.id)}
                      />
                      {" "}{getLocalizedField(c, "name", lang)}
                      <span className="count">{c.count.toLocaleString()}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* 城市 */}
            {(filters.cities || []).length > 0 && (
              <div className="filter-group">
                <div className="filter-group-title">
                  {t("search.city")}
                  {cityId && <span className="clear" onClick={() => { setCityId(null); setPageNum(1); }}>{t("search.reset")}</span>}
                </div>
                <div className="filter-options">
                  {(filters.cities || []).map(c => (
                    <label key={c.id} className="filter-option" style={{ cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={cityId === c.id}
                        onChange={() => toggleFilter(setCityId, c.id)}
                      />
                      {" "}{getLocalizedField(c, "name", lang)}
                      <span className="count">{c.count.toLocaleString()}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* 燃油类型 */}
            {filters.fuelTypes && Object.keys(filters.fuelTypes).length > 0 && (
              <div className="filter-group">
                <div className="filter-group-title">
                  {t("search.fuelType")}
                  {fuelType && <span className="clear" onClick={() => { setFuelType(null); setPageNum(1); }}>{t("search.reset")}</span>}
                </div>
                <div className="filter-options">
                  {Object.entries(filters.fuelTypes).map(([key, count]) => (
                    <label key={key} className="filter-option" style={{ cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={fuelType === key}
                        onChange={() => toggleFilter(setFuelType, key)}
                      />
                      {" "}{getFuelLabel(key)}
                      <span className="count">{count.toLocaleString()}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* 变速箱 */}
            {filters.transmissions && Object.keys(filters.transmissions).length > 0 && (
              <div className="filter-group">
                <div className="filter-group-title">
                  {t("search.transmission")}
                  {transmission && <span className="clear" onClick={() => { setTransmission(null); setPageNum(1); }}>{t("search.reset")}</span>}
                </div>
                <div className="filter-options">
                  {Object.entries(filters.transmissions).map(([key, count]) => (
                    <label key={key} className="filter-option" style={{ cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={transmission === key}
                        onChange={() => toggleFilter(setTransmission, key)}
                      />
                      {" "}{getTransLabel(key)}
                      <span className="count">{count.toLocaleString()}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <button className="btn btn-primary btn-block" onClick={handleApplyFilter}>{t("search.apply")}</button>
          </aside>

          <main>
            <div className="list-header">
              <div className="list-count">
                {t("search.subtitle", { total: totalCount.toLocaleString() })}
              </div>
            </div>

            {/* Active keyword chip — with × to clear */}
            {keyword.trim() && (
              <div className="active-filters-bar" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 16, fontSize: 13 }}>
                <span style={{ color: "var(--color-text-muted)" }}>{t("search.activeFilters")}:</span>
                <span className="active-filter-chip" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", background: "var(--color-brand-lighter, rgba(83,201,6,0.1))", color: "var(--color-brand)", borderRadius: 16, fontWeight: 500 }}>
                  {t("search.keywordChip", { q: keyword.trim() })}
                  <button
                    type="button"
                    aria-label={t("search.clearKeyword")}
                    onClick={() => { setKeyword(""); setPageNum(1); }}
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 16, lineHeight: 1, color: "var(--color-brand)", fontWeight: 700 }}
                  >×</button>
                </span>
              </div>
            )}

            <div className="car-list">
              {isLoading ? (
                <>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="car-card" style={{ animation: "pulse 1.5s ease-in-out infinite" }}>
                      <div className="car-card-image" style={{ background: "var(--color-bg-alt)" }}/>
                      <div className="car-card-body">
                        <div style={{ height: 16, width: "70%", background: "var(--color-bg-alt)", borderRadius: 4, marginBottom: 8 }}/>
                        <div style={{ height: 14, width: "50%", background: "var(--color-bg-alt)", borderRadius: 4, marginBottom: 8 }}/>
                        <div style={{ height: 20, width: "40%", background: "var(--color-bg-alt)", borderRadius: 4 }}/>
                      </div>
                    </div>
                  ))}
                </>
              ) : records.length === 0 ? (
                <div style={{ padding: "60px 0", textAlign: "center", color: "var(--color-text-muted)", gridColumn: "1/-1" }}>
                  <img src="https://iyoubucket.oss-cn-hangzhou.aliyuncs.com/guineemall/home/no-car.png" alt={t("search.noResults")} width={120} height={120} style={{ display: "block", margin: "0 auto 12px", objectFit: "contain" }} />
                  <div style={{ fontSize: 14, opacity: 1 }}>{t("search.noResultsHint")}</div>
                </div>
              ) : (
                <>
                  {records.map((car) => (
                    <Link key={car.id} href={langHref(`/detail/${car.id}`)} className="car-card" style={{ textDecoration: "none", color: "inherit" }}>
                      <div className="car-card-image">
                        <img src={car.coverUrl} alt={car.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        <div className="car-card-badges">
                          {car.isVerified === 1 && <span className="tag tag-brand">{t("car.verified")}</span>}
                          {car.isFeatured === 1 && <span className="tag tag-success">{t("car.promoted")}</span>}
                          {car.fuelType === "electric" && car.condition === "new" && <span className="tag tag-new">{t("car.evNew")}</span>}
                        </div>
                        <div className="car-card-tour">📷 {car.imageCount}</div>
                        <button className={`car-card-fav ${favoritedIds.has(car.id) ? "is-favorited" : ""}`} type="button" aria-label={t("car.favorites")} onClick={(e) => handleFavorite(e, car.id)}>
                          <svg viewBox="0 0 24 24" fill={favoritedIds.has(car.id) ? "#EF4444" : "none"} stroke={favoritedIds.has(car.id) ? "#EF4444" : "currentColor"} strokeWidth={2} width={16} height={16}>
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                          </svg>
                        </button>
                      </div>
                      <div className="car-card-body">
                        <div className="car-card-title">{car.title}</div>
                        <div className="car-card-subtitle">
                          📍 {getLocalizedField(car, "cityName", lang)}{car.engineSize ? ` · ${car.engineSize}L` : ""} · {car.transmission === "automatic" ? t("car.auto") : t("car.manual")}
                        </div>
                        <div className={`car-card-price ${car.priceNegotiable === 1 ? "negotiable" : ""}`}>
                          {car.priceNegotiable === 1 ? t("car.negotiable") : formatPrice(car.priceGnf)}
                        </div>
                        <div className="car-card-meta">
                          <span>📏 {car.mileage ? `${(car.mileage / 1000).toFixed(0)}K ${t("car.km")}` : `0 ${t("car.km")}`}</span>
                          <span>
                            {car.fuelType === "electric" ? "⚡" : "⛽"} {t(`car.${car.fuelType}`) || car.fuelType}
                          </span>
                          <span>📅 {car.carYear}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </>
              )}
            </div>

            {totalPages > 1 && (
              <div className="pagination mt-8 mb-12">
                <button
                  disabled={pageNum <= 1}
                  onClick={() => setPageNum(1)}
                >«</button>
                <button
                  disabled={pageNum <= 1}
                  onClick={() => setPageNum(prev => prev - 1)}
                >‹</button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNumBtn;
                  if (totalPages <= 5) {
                    pageNumBtn = i + 1;
                  } else if (pageNum <= 3) {
                    pageNumBtn = i + 1;
                  } else if (pageNum >= totalPages - 2) {
                    pageNumBtn = totalPages - 4 + i;
                  } else {
                    pageNumBtn = pageNum - 2 + i;
                  }
                  return (
                    <button
                      key={pageNumBtn}
                      className={pageNum === pageNumBtn ? "active" : ""}
                      onClick={() => setPageNum(pageNumBtn)}
                    >{pageNumBtn}</button>
                  );
                })}
                <button
                  disabled={pageNum >= totalPages}
                  onClick={() => setPageNum(prev => prev + 1)}
                >›</button>
                <button
                  disabled={pageNum >= totalPages}
                  onClick={() => setPageNum(totalPages)}
                >»</button>
              </div>
            )}
          </main>
        </div>
      </div>

      {toast && (
        <div style={{
          position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 9999,
          padding: "10px 24px", borderRadius: 8, fontSize: 14, fontWeight: 500,
          background: toast.type === "success" ? "#53c906" : "#EF4444",
          color: "#fff", boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          transition: "opacity 0.3s",
        }}>
          {toast.msg}
        </div>
      )}
    </>
  );
}